import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Modal, Form, Input, Tag, message } from 'antd';
import api from '../../../utils/api';

const { Title, Paragraph } = Typography;

const UsersRoles = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]); // full roles array from settings (with permissions)

  const columns = [
    { title: 'Key', dataIndex: 'key', key: 'key', width: 160 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Users', dataIndex: 'users', key: 'users', width: 100 },
    { title: 'Permissions', dataIndex: 'permissions', key: 'permissions', render: (perms = []) => (
        <Space wrap>
          {perms.slice(0, 4).map(p => <Tag key={p}>{p}</Tag>)}
          {perms.length > 4 ? <Tag>+{perms.length - 4} more</Tag> : null}
        </Space>
      )
    },
    { title: 'Actions', key: 'actions', width: 220, render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>Edit</Button>
          <Button size="small" onClick={() => onManagePerms(record)}>Manage Perms</Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Load roles from settings
        const settings = await api.get('/settings/roles-permissions');
        const loadedRoles = Array.isArray(settings?.roles) ? settings.roles : [];
        setRoles(loadedRoles);

        // Load users once and count per role
        const usersResp = await api.get('/users?limit=500');
        const users = Array.isArray(usersResp?.items) ? usersResp.items : [];
        const counts = users.reduce((acc, u) => { const k = u.role || 'unknown'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});

        // Map to table data
        const rows = loadedRoles.map(r => ({
          key: r.key,
          name: r.name,
          description: r.description || '',
          users: counts[r.key] || 0,
          permissions: Object.keys(r.permissions || {}),
        }));
        setData(rows);
      } catch (e) {
        message.error(e.message || 'Failed to load roles');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ key: record.key, name: record.name, description: record.description });
    setOpen(true);
  };

  const onManagePerms = (record) => {
    message.info(`Navigate to manage permissions for role: ${record.name}`);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // Persist by updating settings roles array
      let nextRoles = roles.slice();
      if (editing) {
        nextRoles = nextRoles.map(r => r.key === editing.key ? { ...r, name: values.name, description: values.description } : r);
      } else {
        // create with empty permissions object; manage permissions on the RolesPermissions page
        nextRoles = [{ key: values.key, name: values.name, description: values.description || '', permissions: {} }, ...nextRoles];
      }
      await api.put('/settings/roles-permissions', { roles: nextRoles });
      setRoles(nextRoles);

      // Rebuild table rows
      const usersResp = await api.get('/users?limit=500');
      const users = Array.isArray(usersResp?.items) ? usersResp.items : [];
      const counts = users.reduce((acc, u) => { const k = u.role || 'unknown'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
      const rows = nextRoles.map(r => ({ key: r.key, name: r.name, description: r.description || '', users: counts[r.key] || 0, permissions: Object.keys(r.permissions || {}) }));
      setData(rows);

      message.success(editing ? 'Role updated' : 'Role created');
      setOpen(false);
      setEditing(null);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Users' },
        { title: 'Roles' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Role & Permission Settings</Title>
        <Space>
          <Button type="primary" onClick={onCreate}>New Role</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Manage user roles. Hook this up to your backend roles and permissions system.
        </Paragraph>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        open={open}
        title={editing ? 'Edit Role' : 'Create Role'}
        onCancel={() => { setOpen(false); setEditing(null); }}
        onOk={onSubmit}
        confirmLoading={saving}
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="Key" name="key" rules={[{ required: true }]}>
            <Input placeholder="e.g., staff" disabled={!!editing} />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersRoles;
